import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import * as api from "../../api/categoryApi";

// Async thunks
export const createCategory = createAsyncThunk(
  "category/create",
  async (formData, { rejectWithValue }) => {
    try {
      const response = await api.createCategoryApi(formData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const getCategories = createAsyncThunk(
  "category/getAll",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.getCategoriesApi();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const getCategoryById = createAsyncThunk(
  "category/getById",
  async (categoryId, { rejectWithValue }) => {
    try {
      const response = await api.getCategoryByIdApi(categoryId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const updateCategory = createAsyncThunk(
  "category/update",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await api.updateCategoryApi(id, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const deleteCategory = createAsyncThunk(
  "category/delete",
  async (categoryId, { rejectWithValue }) => {
    try {
      await api.deleteCategoryApi(categoryId);
      return categoryId; // Return the deleted category ID
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

// // Toggle Category
export const toggleCategory = createAsyncThunk(
  "category/toggle",
  async (id, { rejectWithValue }) => {
    try {
      const res = await api.toggleCategoryApi(id);
      return res.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

// Initial state
const initialState = {
  categories: [],
  currentCategory: null,
  loading: false,
  error: null,
  success: false,
  message: "",
};

const categorySlice = createSlice({
  name: "category",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSuccess: (state) => {
      state.success = false;
      state.message = "";
    },
    clearCurrentCategory: (state) => {
      state.currentCategory = null;
    },
    resetCategoryState: (state) => {
      state.categories = [];
      state.currentCategory = null;
      state.loading = false;
      state.error = null;
      state.success = false;
      state.message = "";
    },
  },
  extraReducers: (builder) => {
    builder
      // Create category
      .addCase(createCategory.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
        state.message = "";
      })
      .addCase(createCategory.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.message =
        action.payload.message || "Category created successfully";
        state.categories.unshift(action.payload.data);
      })
      .addCase(createCategory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.success = false;
      })

      // Get all categories
      .addCase(getCategories.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getCategories.fulfilled, (state, action) => {
        state.loading = false;
        state.categories = action.payload.data || [];
      })
      .addCase(getCategories.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Get category by ID
      .addCase(getCategoryById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getCategoryById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentCategory = action.payload.data;
      })
      .addCase(getCategoryById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Update category
      .addCase(updateCategory.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
        state.message = "";
      })
      .addCase(updateCategory.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.message =
          action.payload.message || "Category updated successfully";

        // Update in categories array
        const updatedCategory = action.payload.data;
        const index = state.categories.findIndex(
          (cat) => cat._id === updatedCategory._id,
        );
        if (index !== -1) {
          state.categories[index] = updatedCategory;
        }

        // Update current category if it's the one being edited
        if (
          state.currentCategory &&
          state.currentCategory._id === updatedCategory._id
        ) {
          state.currentCategory = updatedCategory;
        }
      })
      .addCase(updateCategory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.success = false;
      })

      // Delete category
      .addCase(deleteCategory.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
        state.message = "";
      })
      .addCase(deleteCategory.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.message = "Category deleted successfully";

        // Remove from categories array
        state.categories = state.categories.filter(
          (cat) => cat._id !== action.payload,
        );

        // Clear current category if it's the one being deleted
        if (
          state.currentCategory &&
          state.currentCategory._id === action.payload
        ) {
          state.currentCategory = null;
        }
      })
      .addCase(deleteCategory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.success = false;
      })

      // Toggle category
      .addCase(toggleCategory.pending, (state) => {
        state.loading = true;
      })
      .addCase(toggleCategory.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;

        const updated = action.payload;

        // categories list update
        const index = state.categories.findIndex(
          (cat) => cat._id === updated._id,
        );
        if (index !== -1) {
          state.categories[index] = updated;
        }

        // currentCategory update
        if (
          state.currentCategory &&
          state.currentCategory._id === updated._id
        ) {
          state.currentCategory = updated;
        }
      })
      .addCase(toggleCategory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const {
  clearError,
  clearSuccess,
  clearCurrentCategory,
  resetCategoryState,
} = categorySlice.actions;

// Selectors
export const selectCategories = (state) => state.category.categories;
export const selectCurrentCategory = (state) => state.category.currentCategory;
export const selectCategoryLoading = (state) => state.category.loading;
export const selectCategoryError = (state) => state.category.error;
export const selectCategorySuccess = (state) => state.category.success;
export const selectCategoryMessage = (state) => state.category.message;

export default categorySlice.reducer;
